import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/api"
import { PERSONALIZED_SUMMARY_QUERY_KEY } from "./usePersonalizedSummary"

interface PersonalizedSummaryRequest {
    profileId: string
    billId?: number
    aiTrackingBoardId?: number
}

export interface PersonalizedSummary {
    id: string
    summary: string
    billId: number
    billTitle: string
    profileId: string
    profileName: string
    createdAt: string
}

export class RateLimitError extends Error {
    retryAfterSeconds: number
    retryAtTime: Date

    constructor(message: string, retryAfterSeconds: number) {
        super(message)
        this.name = "RateLimitError"
        this.retryAfterSeconds = retryAfterSeconds
        this.retryAtTime = new Date(Date.now() + retryAfterSeconds * 1000)
    }
}

// API function to generate personalized summary
const generatePersonalizedSummary = async ({ boardId, billId, profileId }): Promise<PersonalizedSummary> => {
    const response = await api.post(
        "/api/personalization/bill-summaries/",
        {
            bill_id: billId,
            ai_tracking_board_id: boardId,
            personalization_profile_id: profileId,
            preview: true,
        },
        { throwOnNotOk: false },
    )

    // Handle rate limit errors
    if (response.status === 429) {
        const errorData = await response.json()
        throw new RateLimitError(
            errorData.error || "Rate limit exceeded. Please try again later.",
            errorData.retry_after_seconds || 300,
        )
    }

    // Handle other errors
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    return {
        id: data.summary.id?.toString(),
        summary: data.summary.content,
        billId: data.summary.bill_id,
        billTitle: data.summary.bill_title,
        profileId: data.summary.profile_id,
        profileName: data.summary.profile_name,
        createdAt: data.summary.created_at,
    }
}

export const usePersonalizedSummaryGeneration = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: generatePersonalizedSummary,
        onSuccess: (data, variables) => {
            // Invalidate the personalized summary cache to refresh the data
            queryClient.invalidateQueries({
                queryKey: [PERSONALIZED_SUMMARY_QUERY_KEY, variables.billId, variables.boardId, variables.profileId],
            })

            // Also invalidate any bill-related queries that might show latest_personalized_summary
            queryClient.invalidateQueries({
                queryKey: ["ai-tracking-board-bills"],
            })
        },
    })
}
